
import React, { useState, useEffect } from 'react';
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Route, MapPinned, House, Mail, Check } from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { callRpcFunction } from "@/utils/rpcUtils";

const ComingSoon = () => {
  const [interestCount, setInterestCount] = useState<number>(0);
  const [hasRegistered, setHasRegistered] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  // Load interest count on component mount
  useEffect(() => {
    const loadInterestCount = async () => {
      try {
        // Use our typed callRpcFunction helper
        const { data, error } = await callRpcFunction('get_power_washing_interest_count');
        
        if (error) {
          console.error("Error loading interest count:", error);
          return;
        }
        
        if (data !== null) {
          setInterestCount(data);
        }
      } catch (err) {
        console.error("Failed to load interest count:", err);
      }
    };

    // Check if user has already registered interest (using localStorage)
    const registeredInterest = localStorage.getItem('registered_power_washing_interest');
    if (registeredInterest === 'true') {
      setHasRegistered(true);
    }

    loadInterestCount();
  }, []);

  const handleRegisterInterest = async () => {
    if (hasRegistered || isLoading) return;
    
    setIsLoading(true);
    
    try {
      // Update the interest count in the database using our RPC function
      const { error } = await callRpcFunction('increment_power_washing_interest');
      
      if (error) {
        console.error("Error registering interest:", error);
        toast.error("Failed to register interest. Please try again later.");
        setIsLoading(false);
        return;
      }
      
      // Set local state to reflect registration
      setInterestCount(prevCount => prevCount + 1);
      setHasRegistered(true);
      
      // Store in localStorage to prevent multiple clicks
      localStorage.setItem('registered_power_washing_interest', 'true');
      
      toast.success("Thanks for your interest! We'll keep you updated.");
    } catch (err) {
      console.error("Failed to register interest:", err);
      toast.error("Something went wrong. Please try again later.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white">
      <div className="container mx-auto px-4 py-16">
        <div className="text-center mb-12">
          <h1 className="text-4xl md:text-6xl font-bold text-gray-900 mb-4">
            Power Washing Services
          </h1>
          <p className="text-xl text-gray-600 mb-8">
            Professional cleaning solutions coming soon for:
          </p>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-4xl mx-auto mb-12">
            <Card className="transform hover:scale-105 transition-transform duration-300">
              <CardContent className="p-6 text-center">
                <Route className="w-12 h-12 mx-auto mb-4 text-blue-600" />
                <h3 className="text-xl font-semibold mb-2">Driveways</h3>
                <p className="text-gray-600">Restore your driveway's appearance</p>
              </CardContent>
            </Card>

            <Card className="transform hover:scale-105 transition-transform duration-300">
              <CardContent className="p-6 text-center">
                <MapPinned className="w-12 h-12 mx-auto mb-4 text-blue-600" />
                <h3 className="text-xl font-semibold mb-2">Paths</h3>
                <p className="text-gray-600">Clean and safe walkways</p>
              </CardContent>
            </Card>

            <Card className="transform hover:scale-105 transition-transform duration-300">
              <CardContent className="p-6 text-center">
                <House className="w-12 h-12 mx-auto mb-4 text-blue-600" />
                <h3 className="text-xl font-semibold mb-2">More Services</h3>
                <p className="text-gray-600">Patios, decks, and exteriors</p>
              </CardContent>
            </Card>
          </div>

          <div className="bg-white p-8 rounded-lg shadow-md max-w-lg mx-auto mb-10">
            <h2 className="text-2xl font-bold mb-4">Register Your Interest</h2>
            <p className="text-gray-600 mb-6">
              Be the first to know when our power washing services launch in your area. 
              No commitment required - we'll simply notify you when we're ready!
            </p>
            
            <div className="flex flex-col items-center">
              <Button 
                onClick={handleRegisterInterest}
                disabled={hasRegistered || isLoading}
                size="lg" 
                className="mb-4"
                variant={hasRegistered ? "outline" : "default"}
              >
                {isLoading ? (
                  "Registering..."
                ) : hasRegistered ? (
                  <>
                    <Check className="mr-2" /> Interest Registered
                  </>
                ) : (
                  <>
                    <Mail className="mr-2" /> Register Interest
                  </>
                )}
              </Button>
              
              <div className="text-sm text-gray-500 font-medium">
                {interestCount} {interestCount === 1 ? 'person has' : 'people have'} registered interest so far
              </div>
            </div>
          </div>

          <div className="space-x-4">
            <Button asChild>
              <Link to="/" className="inline-flex items-center">
                Back to Home
              </Link>
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ComingSoon;
